/**
 * Redirect URL utilities for Supabase auth
 * Handles different environments (development vs production)
 */

/**
 * Get the correct redirect URL based on environment
 * @returns {string} The appropriate redirect URL
 */
export const getRedirectUrl = () => {
  // Optional canonical site (e.g. one hostname when you serve the same build on multiple domains)
  const fromEnv =
    typeof process !== 'undefined' && process.env.REACT_APP_SITE_URL
      ? String(process.env.REACT_APP_SITE_URL).replace(/\/$/, '')
      : '';
  if (fromEnv) return fromEnv;

  // Must match the origin where the user started auth. With PKCE (`flowType: 'pkce'` in
  // `supabase.js`), the code verifier lives in this origin's storage; if `redirectTo` in the
  // email points at a different host (e.g. hardcoded Vercel while the user uses a custom domain),
  // the reset link cannot be exchanged (or Supabase may fall back to Site URL `/`), so users see
  // "no valid link" or land on the homepage in a recovery session instead of `/reset-password`.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  if (process.env.NODE_ENV === 'production') {
    return 'https://badminton-app-project.vercel.app';
  }
  return 'http://localhost:3000';
};

/**
 * Get redirect URL for password reset
 * @returns {string} Password reset redirect URL
 */
export const getPasswordResetRedirectUrl = () => {
  return `${getRedirectUrl()}/reset-password`;
};

/**
 * Get redirect URL for email confirmation
 * @returns {string} Email confirmation redirect URL
 */
export const getEmailConfirmationRedirectUrl = () => {
  return `${getRedirectUrl()}/`;
};

/**
 * Get redirect URL for signup
 * @returns {string} Signup redirect URL
 */
export const getSignupRedirectUrl = () => {
  return `${getRedirectUrl()}/`;
};

/**
 * Get the OAuth callback URL for signInWithOAuth's redirectTo option.
 * This must be added to the Supabase Dashboard → URL Configuration →
 * Additional Redirect URLs allow-list for every environment.
 * The Google Cloud Console Authorized redirect URI stays at
 * https://<ref>.supabase.co/auth/v1/callback — NOT this app URL.
 * @returns {string}
 */
export const getOAuthCallbackUrl = () => {
  return `${getRedirectUrl()}/auth/callback`;
};
